import { deleteJson, getJson, postJson, putJson } from '@/shared/api/apiClient';

/*
 * The workpad: a per-video notebook whose entries are pinned to a moment in the
 * video, plus the paper's appearance.
 *
 * Notes are backed by `/api/v1/videos/{id}/notes`; the appearance settings by
 * `/api/v1/preferences/workpad`, because they are per user rather than per
 * video — someone who wants ruled mint paper wants it everywhere.
 *
 * One thing worth knowing before editing this file: `html` is user-authored rich
 * text that gets rendered back through `dangerouslySetInnerHTML`, and **this
 * file does not sanitise it**. `Workpad.tsx` does, on all three edges — the
 * draft before it is saved, the stored html before it is rendered, and each
 * edit before it is sent — so everything reaching `addNote`/`updateNote` from
 * the app has already been through `sanitizeNoteHtml`.
 *
 * Which is to say the guarantee belongs to the caller, not to the service: a
 * new call site that skips it would post whatever it was handed. The server
 * sanitises too, and that is the one that actually holds, because it is the
 * only one covering a request that never came from this client. What comes back
 * from the API is what was stored, which may be less than what was sent.
 */

const notesPath = (videoId: string) =>
  `/api/v1/videos/${encodeURIComponent(videoId)}/notes`;
const PREFS_PATH = '/api/v1/preferences/workpad';

/** The six paper colours from the Figma workpad settings popover. */
export const PAPER_COLORS = ['white', 'pink', 'olive', 'mint', 'blue', 'purple'] as const;
export type PaperColor = (typeof PAPER_COLORS)[number];

export type Note = {
  id: string;
  /** Seconds into the video this note was taken at. */
  timestamp: number;
  /** Rich text as HTML, produced by the contenteditable workpad. */
  html: string;
  updatedAt: string;
};

export type WorkpadPrefs = {
  paper: PaperColor;
  /** Ruled lines behind the text. */
  lines: boolean;
  /** Show every note's timestamp, rather than only on hover. */
  timestamps: boolean;
};

export const DEFAULT_WORKPAD_PREFS: WorkpadPrefs = {
  paper: 'white',
  lines: true,
  timestamps: false,
};

/**
 * Play order: earliest moment in the video first.
 *
 * Every one of the four calls below returns the whole list and every one of
 * them runs it through this, because the workpad renders `notes` in array order
 * and nothing between here and the DOM sorts anything.
 *
 * `api-reference.md` asks the server for this order, and asking is not the same
 * as getting: a list endpoint returns whatever the query returned unless
 * something made it do otherwise, and insertion order is the usual default.
 * That is what shipped — notes drawn in the order they were written rather than
 * the order they play, so adding one about the opening after one about the
 * ending put the two the wrong way round and every note after them was wrong
 * too. The localStorage implementation sorted here and the conversion to HTTP
 * moved the guarantee into a comment, which is not a place a guarantee lives.
 *
 * Ties break on `id` rather than being left to the server. Two notes on the
 * same second are common — pause, write, write again — and an arbitrary but
 * *stable* order beats one that reshuffles on every request.
 */
function inPlayOrder(notes: Note[]): Note[] {
  return [...notes].sort((a, b) => a.timestamp - b.timestamp || a.id.localeCompare(b.id));
}

export async function listNotes(_userId: string, videoId: string): Promise<Note[]> {
  return inPlayOrder(await getJson<Note[]>(notesPath(videoId)));
}

export async function addNote(
  _userId: string,
  videoId: string,
  timestamp: number,
  html: string,
): Promise<Note[]> {
  return inPlayOrder(
    await postJson<Note[]>(notesPath(videoId), {
      timestamp: Math.max(0, timestamp),
      html,
    }),
  );
}

export async function updateNote(
  _userId: string,
  videoId: string,
  noteId: string,
  html: string,
): Promise<Note[]> {
  return inPlayOrder(
    await putJson<Note[]>(`${notesPath(videoId)}/${encodeURIComponent(noteId)}`, { html }),
  );
}

export async function deleteNote(
  _userId: string,
  videoId: string,
  noteId: string,
): Promise<Note[]> {
  return inPlayOrder(
    await deleteJson<Note[]>(`${notesPath(videoId)}/${encodeURIComponent(noteId)}`),
  );
}

/* Preferences are per user, not per video — someone who wants ruled mint paper
   wants it everywhere. Defaults are applied client-side; the server returns {}
   for a user who has never set anything, so the design decision lives in one
   place. */
export async function getWorkpadPrefs(_userId: string): Promise<WorkpadPrefs> {
  return {
    ...DEFAULT_WORKPAD_PREFS,
    ...(await getJson<Partial<WorkpadPrefs>>(PREFS_PATH)),
  };
}

export async function setWorkpadPrefs(
  _userId: string,
  prefs: Partial<WorkpadPrefs>,
): Promise<WorkpadPrefs> {
  // The server shallow-merges, so sending only what changed is enough.
  const stored = await putJson<Partial<WorkpadPrefs>>(PREFS_PATH, prefs);
  return { ...DEFAULT_WORKPAD_PREFS, ...stored };
}
